import type { ComponentProps } from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@repo/lib/cn';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

export function Select(selectProps: ComponentProps<typeof SelectPrimitive.Root>) {
  const { ...props } = selectProps;
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

export function SelectGroup(selectGroupProps: ComponentProps<typeof SelectPrimitive.Group>) {
  const { className, ...props } = selectGroupProps;
  return <SelectPrimitive.Group data-slot="select-group" className={cn('text-muted-accent scroll-my-1 p-1', className)} {...props} />;
}

export function SelectValue(selectValueProps: ComponentProps<typeof SelectPrimitive.Value>) {
  const { ...props } = selectValueProps;
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

export function SelectTrigger(
  selectTriggerProps: ComponentProps<typeof SelectPrimitive.Trigger> & {
    showIcon?: boolean;
    size?: 'sm' | 'md';
  }
) {
  const { className, showIcon = true, size = 'md', children, ...props } = selectTriggerProps;

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-border-primary bg-input dark:bg-input/35 cursor-pointer hover:bg-input-focus dark:hover:bg-input-focus/75 data-placeholder:text-muted-foreground focus-visible:border-border-primary-focus outline-0 outline-focus-ring focus-visible:outline-3 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive aria-invalid:border-destructive/75 gap-2 rounded-md border pr-2 pl-2.5 text-base transition-all duration-150 select-none data-[size=md]:h-10 data-[size=md]:min-h-10 data-[size=sm]:h-9 data-[size=sm]:min-h-9 *:data-[slot=select-value]:gap-1.5 [&_svg:not([class*='size-'])]:size-4 flex items-center justify-between whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      {showIcon ? (
        <SelectPrimitive.Icon className="text-muted-foreground">
          <ChevronDownIcon className="pointer-events-none size-4" />
        </SelectPrimitive.Icon>
      ) : null}
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent(selectContentProps: ComponentProps<typeof SelectPrimitive.Content>) {
  const { className, children, position = 'item-aligned', align = 'center', ...props } = selectContentProps;
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        data-align-trigger={position === 'item-aligned'}
        className={cn(
          'bg-popover text-accent-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border border-border-primary min-w-36 rounded-md shadow-md duration-150 relative z-50 max-h-(--radix-select-content-available-height) origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto data-[align-trigger=true]:animate-none',
          position === 'popper' && 'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'data-[position=popper]:h-(--radix-select-trigger-height) data-[position=popper]:w-full data-[position=popper]:min-w-(--radix-select-trigger-width)',
            position === 'popper' && ''
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectLabel(selectLabelProps: ComponentProps<typeof SelectPrimitive.Label>) {
  const { className, ...props } = selectLabelProps;
  return <SelectPrimitive.Label data-slot="select-label" className={cn('text-muted-foreground px-1.5 py-1 text-sm', className)} {...props} />;
}

export function SelectItem(selectItemProps: ComponentProps<typeof SelectPrimitive.Item>) {
  const { className, children, ...props } = selectItemProps;
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent/80 focus:text-accent-foreground cursor-pointer transition-colors duration-150 not-data-[variant=destructive]:focus:**:text-accent-foreground gap-1.5 rounded-sm py-1 pr-8 pl-1.5 text-base [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 relative flex w-full items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="pointer-events-none" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator(selectSeparatorProps: ComponentProps<typeof SelectPrimitive.Separator>) {
  const { className, ...props } = selectSeparatorProps;
  return <SelectPrimitive.Separator data-slot="select-separator" className={cn('bg-border-primary h-px pointer-events-none', className)} {...props} />;
}

export function SelectScrollUpButton(selectScrollUpButtonProps: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  const { className, ...props } = selectScrollUpButtonProps;
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn("bg-popover z-10 flex cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

export function SelectScrollDownButton(selectScrollDownButtonProps: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  const { className, ...props } = selectScrollDownButtonProps;
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn("bg-popover z-10 flex cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      <ChevronDownIcon className="size-3.5" />
    </SelectPrimitive.ScrollDownButton>
  );
}
